/**
 * RecurringItem
 * One row per detected recurring transaction (subscription, salary, etc.)
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import type { RecurringTransaction } from '../../api/endpoints/budget';

const CATEGORY_ICONS: Record<string, string> = {
  housing: 'home',
  food: 'food',
  transport: 'car',
  insurance: 'shield-check',
  subscriptions: 'cellphone',
  leisure: 'gamepad-variant',
  health: 'heart-pulse',
  taxes: 'file-document',
  education: 'school',
  family_assistance: 'account-group',
  children_expenses: 'baby-carriage',
  debt_repayment: 'bank-transfer',
  shopping: 'shopping',
  savings: 'piggy-bank',
  salary: 'cash',
  bank_fees: 'alert-circle-outline',
  other: 'dots-horizontal',
};

const CATEGORY_COLORS: Record<string, string> = {
  housing: '#E8F5E9',
  food: '#FFF3E0',
  transport: '#E3F2FD',
  insurance: '#F3E5F5',
  subscriptions: '#E8EAF6',
  leisure: '#FFF8E1',
  health: '#FFEBEE',
  salary: '#E8F5E9',
  bank_fees: '#FFEBEE',
};

const FREQ_LABELS: Record<string, string> = {
  weekly: '/sem',
  monthly: '/mois',
  quarterly: '/trim',
  yearly: '/an',
};

interface Props {
  item: RecurringTransaction;
}

export default function RecurringItem({ item }: Props) {
  const iconName = CATEGORY_ICONS[item.category] || 'dots-horizontal';
  const iconBg = CATEGORY_COLORS[item.category] || '#F3F4F6';
  const freqLabel = FREQ_LABELS[item.frequency] || '/mois';
  const isExpense = item.transaction_type === 'expense';

  // Title case the description
  const title = item.description
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

  // Status badge
  const badge = getBadge(item);

  return (
    <View style={styles.container}>
      <View style={[styles.iconContainer, { backgroundColor: iconBg }]}>
        <Icon name={iconName} size={20} color={colors.text} />
      </View>

      <View style={styles.content}>
        <View style={styles.topRow}>
          <Text style={styles.title} numberOfLines={1}>{title}</Text>
          <Text style={[styles.amount, isExpense && { color: '#EF4444' }]}>
            {isExpense ? '-' : '+'}{Number(item.amount || 0).toLocaleString('fr-FR')} €{freqLabel}
          </Text>
        </View>

        <View style={styles.bottomRow}>
          {item.usual_day > 0 && (
            <Text style={styles.detail}>le {item.usual_day}/{item.frequency === 'monthly' ? 'mois' : ''}</Text>
          )}
          {badge && (
            <View style={[styles.badge, { backgroundColor: badge.bg }]}>
              <Text style={[styles.badgeText, { color: badge.color }]}>{badge.label}</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

function getBadge(item: RecurringTransaction): { label: string; bg: string; color: string } | null {
  if (item.is_new) {
    return { label: 'Nouveau', bg: '#EFF6FF', color: '#3B82F6' };
  }
  if (item.price_change) {
    const change = Number(item.price_change.change) || 0;
    if (change > 0) {
      return {
        label: `Hausse +${Math.round(change)} €`,
        bg: '#FFF7ED',
        color: '#EA580C',
      };
    }
  }
  // Check if next_expected is in the past (missed)
  if (item.next_expected) {
    const nextDate = new Date(item.next_expected);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (nextDate < today) {
      return { label: 'Pas encore passé', bg: '#FFF7ED', color: '#EA580C' };
    }
  }
  return null;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    marginBottom: spacing.xs,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  content: {
    flex: 1,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  title: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.text,
    marginRight: spacing.sm,
  },
  amount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  detail: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: typography.fontWeight.medium,
  },
});
